import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import { ThemeProvider } from "./context/ThemeContext";
import { LanguageProvider } from "./context/LanguageContext";
import { Toaster } from "./components/ui/sonner";
import { DocumentViewerHost } from "./components/common/DocumentViewerHost";
import { useBranding } from "./hooks/useBranding";

// Pages
import LoginPage from "./pages/LoginPage";
import CompanySetupPage from "./pages/CompanySetupPage";
import DashboardPage from "./pages/DashboardPage";
import POSStandalonePage from "./pages/POSStandalonePage";
import TablesPage from "./pages/TablesPage";
import OrdersPage from "./pages/OrdersPage";
import ProductsPage from "./pages/ProductsPage";
import StockPage from "./pages/StockPage";
import EmployeesPage from "./pages/EmployeesPage";
import SettingsPage from "./pages/SettingsPage";
import AuthCallback from "./pages/AuthCallback";
import PublicMenuPage from "./pages/PublicMenuPage";
import InvoicesPage from "./pages/InvoicesPage";
import ReportsPage from "./pages/ReportsPage";
import ReportCenterPage from "./pages/ReportCenterPage";
import KitchenDisplayPage from "./pages/KitchenDisplayPage";
import ReservationsPage from "./pages/ReservationsPage";
import CashRegisterPage from "./pages/CashRegisterPage";
import AdminPanelPage from "./pages/AdminPanelPage";
import SAFTPage from "./pages/SAFTPage";
import SuppliersPage from "./pages/SuppliersPage";
import PurchasesPage from "./pages/PurchasesPage";
import InventoryPage from "./pages/InventoryPage";
import FinancePage from "./pages/FinancePage";
import HRPage from "./pages/HRPage";
import CustomersPage from "./pages/CustomersPage";
import BanksPage from "./pages/BanksPage";
import AuditPage from "./pages/AuditPage";
import AccountingPage from "./pages/AccountingPage";
import QuotesPage from "./pages/QuotesPage";
import AssetsPage from "./pages/AssetsPage";
import WarehousesPage from "./pages/WarehousesPage";
import CRMPage from "./pages/CRMPage";
import PricingPage from "./pages/PricingPage";
import RequisitionsPage from "./pages/RequisitionsPage";
import LotsPage from "./pages/LotsPage";
import ProductionPage from "./pages/ProductionPage";
import LogisticsPage from "./pages/LogisticsPage";
import FleetPage from "./pages/FleetPage";
import MaintenancePage from "./pages/MaintenancePage";
import ProjectsPage from "./pages/ProjectsPage";
import BIPage from "./pages/BIPage";
import OrganizationPage from "./pages/OrganizationPage";
import SecurityPage from "./pages/SecurityPage";
import LandingPage from "./pages/LandingPage";
import PlanPage from "./pages/PlanPage";
import RecurringPage from "./pages/RecurringPage";
import AdsPage from "./pages/AdsPage";
import InsurersPage from "./pages/InsurersPage";
import GiftCardsPage from "./pages/GiftCardsPage";
import BackofficeLoginPage from "./pages/BackofficeLoginPage";
import BackofficePage from "./pages/BackofficePage";
import ProfilePickerPage from "./pages/ProfilePickerPage";

// Layout
import MainLayout from "./components/layout/MainLayout";

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, needsCompanySetup, deviceUnlocked } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">A carregar...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Dispositivo desbloqueado por um admin/gerente → ecrã de perfis + PIN em vez do login clássico
    return <Navigate to={deviceUnlocked ? "/perfis" : "/login"} state={{ from: location }} replace />;
  }

  // Redirecionar para setup da empresa se necessário
  if (needsCompanySetup && location.pathname !== "/setup-company") {
    return <Navigate to="/setup-company" replace />;
  }

  return children;
};

const RootRoute = () => {
  const { isAuthenticated, loading, deviceUnlocked } = useAuth();
  const { branding, loaded } = useBranding();
  if (loading || !loaded) {
    return <div className="min-h-screen bg-[#0A0A0A]" />;
  }
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  if (deviceUnlocked) return <Navigate to="/perfis" replace />;
  // Domínio próprio de uma empresa: a raiz é o ecrã de login com a marca dela (sem a landing da LUMORA)
  return branding.branded ? <Navigate to="/login" replace /> : <LandingPage />;
};

const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id from OAuth callback
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/perfis" element={<ProfilePickerPage />} />
      <Route path="/backoffice/login" element={<BackofficeLoginPage />} />
      <Route path="/backoffice" element={<BackofficePage />} />
      <Route path="/dono" element={<Navigate to="/backoffice" replace />} />
      <Route path="/menu/:tableId" element={<PublicMenuPage />} />
      <Route
        path="/pos"
        element={
          <ProtectedRoute>
            <POSStandalonePage />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/setup-company" 
        element={
          <ProtectedRoute>
            <CompanySetupPage />
          </ProtectedRoute>
        } 
      />
      
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="tables" element={<TablesPage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="stock" element={<StockPage />} />
        <Route path="employees" element={<EmployeesPage />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="saft" element={<SAFTPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="report-center" element={<ReportCenterPage />} />
        <Route path="kitchen" element={<KitchenDisplayPage />} />
        <Route path="reservations" element={<ReservationsPage />} />
        <Route path="cash" element={<CashRegisterPage />} />
        <Route path="suppliers" element={<SuppliersPage />} />
        <Route path="purchases" element={<PurchasesPage />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="finance" element={<FinancePage />} />
        <Route path="hr" element={<HRPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="banks" element={<BanksPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="accounting" element={<AccountingPage />} />
        <Route path="quotes" element={<QuotesPage />} />
        <Route path="assets" element={<AssetsPage />} />
        <Route path="warehouses" element={<WarehousesPage />} />
        <Route path="crm" element={<CRMPage />} />
        <Route path="pricing" element={<PricingPage />} />
        <Route path="requisitions" element={<RequisitionsPage />} />
        <Route path="lots" element={<LotsPage />} />
        <Route path="production" element={<ProductionPage />} />
        <Route path="logistics" element={<LogisticsPage />} />
        <Route path="fleet" element={<FleetPage />} />
        <Route path="maintenance" element={<MaintenancePage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="bi" element={<BIPage />} />
        <Route path="organization" element={<OrganizationPage />} />
        <Route path="security" element={<SecurityPage />} />
        <Route path="plan" element={<PlanPage />} />
        <Route path="recurring" element={<RecurringPage />} />
        <Route path="adscreen" element={<AdsPage />} />
        <Route path="insurers" element={<InsurersPage />} />
        <Route path="giftcards" element={<GiftCardsPage />} />
        <Route path="admin" element={<AdminPanelPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <CurrencyProvider>
            <BrowserRouter>
              <AppRouter />
              <Toaster position="top-right" richColors />
              <DocumentViewerHost />
            </BrowserRouter>
          </CurrencyProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
